import classNames from "clsx";
import { Link, LinkProps, useRouter } from "@tanstack/react-router";
import { type Ref, type ReactElement } from "react";
import { type BaseElementProps } from "../../../types/Element";
import styles from "./Clickable.module.scss";

export type LinkClickableProps = BaseElementProps<
  "a",
  Omit<LinkProps, "children" | "to"> & { type?: never; href: string }
>;

export type ButtonClickableProps = BaseElementProps<"button", { href?: never }>;

export type ClickableProps = (ButtonClickableProps | LinkClickableProps) & {
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>;
};

export function Clickable({
  className,
  as: Tag = "button",
  ref,
  ...restProps
}: ClickableProps): ReactElement {
  const classes = classNames([styles.clickable, className]);
  const router = useRouter();

  // Assume an anchor element if it has a href prop
  if (Tag === "a" || "href" in restProps) {
    const href = restProps.href as string;

    // Check if it's an internal route by trying to match it with existing routes
    const matchedRoutes = router.matchRoutes(href, {
      to: href,
      fuzzy: false,
      partial: false,
    });

    if (matchedRoutes.length > 1) {
      return (
        <Link
          {...(restProps as LinkClickableProps)}
          to={href}
          className={classes}
          ref={ref as Ref<HTMLAnchorElement>}
        />
      );
    } else {
      return (
        <a
          {...(restProps as LinkClickableProps)}
          href={href}
          className={classes}
          ref={ref as Ref<HTMLAnchorElement>}
        />
      );
    }
  }

  // Default to button if no href
  return (
    <Tag
      {...restProps}
      className={classes}
      ref={ref as Ref<HTMLButtonElement>}
    />
  );
}
